import React, { useEffect, useId, useState } from 'react';
import { ClipboardDocumentCheckIcon } from '@heroicons/react/24/outline';
import { useMutation, useQueryClient } from 'react-query';
import { toast } from 'react-hot-toast';
import {
  adminAPI,
  InvoiceCreateRecoveryReview as RecoveryReview,
  InvoiceCreateRecoveryStatus,
} from '../../services/api';
import { Invoice } from '../../types/invoice';
import { formatCurrencyCents } from '../../utils/invoiceFormatting';
import Modal from '../ui/Modal';

type Props = {
  invoice: Invoice;
  status: InvoiceCreateRecoveryStatus;
  onRefresh: () => Promise<Invoice | undefined>;
  onRefreshStatus: () => Promise<InvoiceCreateRecoveryStatus | undefined>;
  onRefreshSync: () => Promise<unknown>;
};

type Reviewed = {
  response: RecoveryReview;
  invoiceSnapshot: string;
  displaySnapshot: string;
  statusSnapshot: string;
};
type RecoveryAction = { kind: 'review'; reason: string } | { kind: 'execute'; reviewed: Reviewed };

function statusSnapshot(status: InvoiceCreateRecoveryStatus) {
  return JSON.stringify([status.eligible, status.reason, status.generation?.state, status.payment_ready]);
}

function expiry(review: RecoveryReview) {
  return Math.min(new Date(review.review.expiresAt).getTime(), new Date(review.review.createdAt).getTime() + 5 * 60_000);
}

export default function InvoiceCreateRecoveryReview({ invoice, status, onRefresh, onRefreshStatus, onRefreshSync }: Props) {
  const queryClient = useQueryClient();
  const reasonId = useId();
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState('');
  const [reviewed, setReviewed] = useState<Reviewed>();
  const [message, setMessage] = useState('');
  const [now, setNow] = useState(Date.now);
  const mutationKey = ['invoice-create-recovery', invoice.id];
  const isBusy = () => queryClient.isMutating({ mutationKey }) + queryClient.isMutating({ mutationKey: ['invoice-sync-retry', invoice.id] }) > 0;
  const pending = React.useSyncExternalStore(
    React.useCallback((notify) => queryClient.getMutationCache().subscribe(notify), [queryClient]),
    isBusy,
  );

  useEffect(() => {
    if (!open || !reviewed) return;
    setNow(Date.now());
    const timer = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(timer);
  }, [open, reviewed]);

  const invoiceSnapshot = JSON.stringify(invoice);
  const currentStatusSnapshot = statusSnapshot(status);
  const expired = Boolean(reviewed && !(expiry(reviewed.response) > now));
  const changed = Boolean(reviewed && (
    (reviewed.invoiceSnapshot !== invoiceSnapshot && reviewed.displaySnapshot !== invoiceSnapshot) ||
    reviewed.statusSnapshot !== currentStatusSnapshot
  ));
  const wasSubmitted = (reviewId: string) => queryClient.getMutationCache().getAll().some((mutation) => {
    const key = mutation.options.mutationKey;
    const action = mutation.state.variables as RecoveryAction | undefined;
    return Array.isArray(key) && key[0] === mutationKey[0] && key[1] === invoice.id &&
      action?.kind === 'execute' && action.reviewed.response.review.id === reviewId;
  });
  const submitted = Boolean(reviewed && wasSubmitted(reviewed.response.review.id));

  async function reconcile() {
    // Each read must still run if another fails after an uncertain provider outcome.
    const results = await Promise.allSettled([onRefresh(), onRefreshStatus(), onRefreshSync()]);
    return {
      invoice: results[0].status === 'fulfilled' ? results[0].value : undefined,
      status: results[1].status === 'fulfilled' ? results[1].value : undefined,
    };
  }

  async function invalidateInvoiceViews() {
    await Promise.allSettled([
      queryClient.invalidateQueries('admin-invoices'),
      queryClient.invalidateQueries('platform-invoices'),
      queryClient.invalidateQueries(['invoice-drawer-host-invoice', invoice.id]),
      queryClient.invalidateQueries(['invoice-drawer-host-payment-activity', invoice.id]),
    ]);
  }

  const mutation = useMutation(async (action: RecoveryAction) => {
    setMessage('');
    if (action.kind === 'review') {
      setReviewed(undefined);
      try {
        const fresh = await reconcile();
        if (!fresh.invoice || fresh.invoice.id !== invoice.id || !fresh.status?.eligible) {
          setMessage('Current invoice and recovery eligibility could not be confirmed. Refresh before requesting a fresh review.');
          return;
        }
        const response = (await adminAPI.reviewInvoiceCreateRecovery(invoice.id, action.reason)).data;
        setNow(Date.now());
        setReviewed({ response, invoiceSnapshot: JSON.stringify(fresh.invoice), displaySnapshot: invoiceSnapshot,
          statusSnapshot: statusSnapshot(fresh.status) });
      } catch {
        const fresh = await reconcile();
        setMessage(`Review was not confirmed. ${fresh.invoice && fresh.status ? 'Invoice and recovery status refreshed.' : 'Could not confirm refreshed state. Reload the invoice.'} Request a fresh review before continuing.`);
      }
      return;
    }

    const packet = action.reviewed;
    try {
      const fresh = await reconcile();
      if (!fresh.invoice || !fresh.status || !fresh.status.eligible ||
          JSON.stringify(fresh.invoice) !== packet.invoiceSnapshot ||
          statusSnapshot(fresh.status) !== packet.statusSnapshot ||
          !(expiry(packet.response) > Date.now())) {
        setMessage('The invoice, recovery status, or review expiry changed. Request a fresh review.');
        return;
      }
      const result = (await adminAPI.executeInvoiceCreateRecovery(invoice.id, {
        reviewId: packet.response.review.id,
        fingerprint: packet.response.review.fingerprint,
        confirmed: true,
      })).data;
      const saved = await reconcile();
      await invalidateInvoiceViews();
      if (result.payment_ready === true && saved.invoice?.id === invoice.id && saved.invoice.status === 'open' &&
          saved.invoice.stripe_invoice_id && saved.invoice.payment_url &&
          saved.invoice.stripe_invoice_id === result.providerInvoiceId) {
        toast.success('Invoice is ready for payment');
        setMessage('Invoice is ready for payment.');
      } else {
        setMessage('Payment readiness is not confirmed. Check the refreshed invoice status before sending a payment request.');
      }
    } catch {
      const fresh = await reconcile();
      setMessage(`Recovery outcome is not confirmed. ${fresh.invoice && fresh.status ? 'Invoice and recovery status refreshed.' : 'Could not confirm refreshed state. Reload the invoice.'} Request a fresh review before continuing.`);
    }
  }, { mutationKey, retry: false });

  function close() {
    if (isBusy()) return;
    setOpen(false);
    setReviewed(undefined);
    setReason('');
    setMessage('');
  }

  if (!status.eligible) return null;

  return (
    <div role="presentation" onClick={(event) => event.stopPropagation()}>
      <button type="button" className="btn-secondary inline-flex items-center gap-1.5 text-xs disabled:opacity-50"
        disabled={pending} onClick={() => setOpen(true)}>
        <ClipboardDocumentCheckIcon className="h-4 w-4" aria-hidden="true" />
        Review invoice recovery
      </button>
      <Modal open={open} onClose={close} title="Review invoice recovery" footer={
        <button type="button" className="btn-secondary" disabled={pending} onClick={close}>Close</button>
      }>
        <div className="space-y-4 text-sm text-on-surface">
          <p className="font-semibold">{invoice.invoice_number}</p>
          <p>No emails will be sent and no payments will be charged.</p>
          <label className="block" htmlFor={reasonId}>Reason for recovery</label>
          <textarea id={reasonId} aria-describedby={`${reasonId}-hint`} className="w-full rounded-md border border-outline p-2" required minLength={5} maxLength={1000}
            rows={3} value={reason} disabled={pending} onChange={(event) => {
              setReason(event.target.value);
              setReviewed(undefined);
            }} />
          <p id={`${reasonId}-hint`} className="text-xs text-on-surface-variant">5 to 1,000 characters. The reason is recorded in the audit review.</p>
          <button type="button" className="btn-secondary disabled:opacity-50"
            disabled={pending || !status.eligible || reason.trim().length < 5 || reason.trim().length > 1000}
            onClick={() => {
              if (!isBusy() && status.eligible && reason.trim().length >= 5 && reason.trim().length <= 1000) {
                mutation.mutate({ kind: 'review', reason: reason.trim() });
              }
            }}>{pending ? 'Recovery request in progress' : reviewed ? 'Request fresh review' : 'Review proposal'}</button>
          {reviewed && <>
            <dl className="grid grid-cols-2 gap-2 break-words">
              <dt>Invoice</dt><dd>{reviewed.response.proposal.invoiceNumber}</dd>
              <dt>Total</dt><dd>{formatCurrencyCents(reviewed.response.proposal.financials.total, reviewed.response.proposal.financials.currency)}</dd>
              <dt>Currency</dt><dd>{reviewed.response.proposal.financials.currency.toUpperCase()}</dd>
              <dt>Amount due</dt><dd>{formatCurrencyCents(reviewed.response.proposal.financials.amountDue, reviewed.response.proposal.financials.currency)}</dd>
              <dt>Application fee</dt><dd>{formatCurrencyCents(reviewed.response.proposal.financials.applicationFee, reviewed.response.proposal.financials.currency)}</dd>
              <dt>Proposed action</dt><dd>{reviewed.response.review.action === 'reuse' ? 'Reuse existing provider invoice' : 'Create provider invoice for this invoice'}</dd>
            </dl>
            <p>This review is bound to your signed-in account and expires after five minutes: {new Date(expiry(reviewed.response)).toLocaleString()}.</p>
            {(expired || changed) && <p role="alert">{expired ? 'Review expired.' : 'Invoice or recovery status changed.'} Request a fresh review.</p>}
            <button type="button" className="btn-primary disabled:opacity-50"
              disabled={pending || expired || changed || submitted || !status.eligible}
              onClick={() => {
                if (!isBusy() && !wasSubmitted(reviewed.response.review.id) &&
                    !changed && status.eligible && expiry(reviewed.response) > Date.now()) {
                  mutation.mutate({ kind: 'execute', reviewed });
                }
              }}>Confirm invoice recovery</button>
          </>}
          {message && <p role="status">{message}</p>}
        </div>
      </Modal>
    </div>
  );
}
